import { FC, useEffect, useState } from 'react';
import { darken, rgba } from 'polished';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { clx } from 'Utils';
import { Colors, GeoAddress } from 'Types';
import { Button, PostalCodeValidator, FontAwesome } from 'Elements';
import { useSearchParams, useRegions } from 'Hooks';
import styles from 'partials/homepage/section/job-search-box.module.scss';

interface Props {
	className?: string;
	setFilterPostalCode?: any;
	isGlobal?: boolean;
	filterPostalCode?: {
		lat: number;
		lon: number;
		postalCode: string;
	};
}

const SearchButton = styled((props: any) => (
	<Button color={Colors.empty} {...props} />
))`
	${({ theme }) =>
		theme.communityPartner.primaryColor &&
		`
			background-color: #96BD68;
			box-shadow: 0 5px 15px ${rgba('#96BD68', 0.2)};
			color: #fff;
			&:hover {
				color: #fff;
				background-color: ${darken(0.06, '#96BD68')};
			}
		`}
`;

export const CommunityPartnerPreviewJobSearchBox: FC<Props> = ({
	className,
	setFilterPostalCode,
	filterPostalCode,
	isGlobal
}) => {
	const { setSearchParams, clearSearchParams } = useSearchParams();
	const query = new URLSearchParams(location.search);
	const { t } = useTranslation();
	const { handleSubmit, register, reset } = useForm<{
		keyword: string;
	}>({
		defaultValues: { keyword: '' }
	});

	const submitSearch: SubmitHandler<{ keyword: string }> = (data) => {
		if (isGlobal) {
			setFilterPostalCode(address);
			if (address?.postalCode === '') {
				clearSearchParams(['page', 'distance']);
				setSearchParams({
					q: data.keyword,
					lat: '',
					lon: ''
				});
				reset({});
			} else {
				setSearchParams({
					lat: address?.lat.toString(),
					lon: address?.lon.toString(),
					postalCode: address?.postalCode,
					q: data.keyword
				});
			}
		} else {
			setSearchParams({ q: data.keyword });
		}
	};

	const [address, setAddress] = useState({
		lat: filterPostalCode?.lat ?? '',
		lon: filterPostalCode?.lon ?? '',
		postalCode: filterPostalCode?.postalCode ?? ''
	});

	const [_isRegionActive, setActiveRegion] = useState(true);
	const { data: activeRegions } = useRegions(1, {
		enabled: false,
		staleTime: Infinity
	});

	useEffect(() => {
		reset({
			keyword: query.get('q')?.toString() ?? ''
		});
	}, [reset, query.get('q')]);

	const checkRegionCode = (regionCode: string) => {
		if (activeRegions) {
			const hasRegion = activeRegions?.some(
				(region) => region.code === regionCode
			);
			setActiveRegion(hasRegion);
		}
	};

	const resetPostalAddress = () => {
		setAddress({
			lat: '',
			lon: '',
			postalCode: ''
		});
	};

	const handlePostalCode = (selected_address: GeoAddress) => {
		const { position, address } = selected_address;
		setAddress({
			lat: position.lat,
			lon: position?.lon,
			postalCode: address.postalCode
		});
		const postalCode = selected_address?.address?.postalCode;
		if (postalCode) {
			const regionCode = postalCode.substring(0, 2);
			checkRegionCode(regionCode);
		}
	};

	const handleAddressChange = (value: string) => {
		if (value == '') {
			resetPostalAddress();
		}
	};

	return (
		<form
			onSubmit={handleSubmit(submitSearch)}
			className={clx('p-0 d-flex gap-2 flex-wrap', className)}>
			<input
				{...register('keyword')}
				type="search"
				className={clx(styles.input, styles.inputbox)}
				placeholder={t('forms.search-keywords')}
				autoComplete="off"
			/>
			{/* {keyword && (
				<Button
					type="button"
					color={Colors.empty}
					className={styles.delete}
					onClick={handleReset}>
					<FontAwesome icon="xmark" size="lg" color="white" />
				</Button>
			)} */}
			{setFilterPostalCode && (
				<PostalCodeValidator
					aria-disabled={isGlobal ? false : true}
					inputValue={filterPostalCode?.postalCode}
					boxClassName={clx(styles.input, styles.inputbox)}
					onChange={handleAddressChange}
					onFind={handlePostalCode}
				/>
			)}
			<SearchButton
				type="submit"
				className={clx(styles.submit, 'd-flex gap-2')}>
				<FontAwesome icon="magnifying-glass" size="lg" />
				<span className="d-none d-md-block">
					{t('button.find-job')}
				</span>
			</SearchButton>
		</form>
	);
};
